import type { UpdateBrandDTO } from "@nlc/shared-types";

import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import { getBrandById } from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

interface UseUpdateBrandParams {
  brandId: number;
}

export function useUpdateBrand({
  brandId,
}: UseUpdateBrandParams): UseMutationResult<unknown, unknown, UpdateBrandDTO> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateBrandDTO) =>
      apiClient.url(`/brands/${brandId}`).patch(data).json(),

    onError: (error: unknown) => {
      const errorMessage =
        (error as { message?: string })?.message || "Failed to update brand";

      toast.add({
        description: errorMessage,
        timeout: 3000,
        title: "Update failed",
        type: "error",
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getBrandById.getBrandById(brandId),
      });

      toast.add({
        description: "Brand updated successfully",
        timeout: 2000,
        title: "Success",
        type: "success",
      });
    },
  });
}
